import { ObjectId } from "bson";
import { TankStockObject } from "./TankStock";
import { fuelTypesToFuelClasses } from "../services/fuelLookup.service";
import moment from "moment";
import { getCarryOver, normalizeId } from "../services/utilities";

const FUEL_SPEC_DATE_FORMAT = "DD/MM/YYYY HH:mm";

const FUEL_SPEC_COMPARISON_KEYS = [
  'bdn_number',
  'fuel_grade',
  'iso_fuel_grade',
  'fuel_class',
  'viscosity',
  'density',
  'lcv',
  'e_value',
  'cf_co2',
  'cf_ch4',
  'cf_n2o',
  'sulphur',
  'mass_at_time_of_blend',
  'parent_1_mass',
  'parent_2_mass',
  'is_component_of_blend',
  'default_spec_id'
]

const parseFuelSpecDate = (value) => {
  if (!value) return null;
  if (moment.isMoment(value)) return value.clone();
  if (value?.$date != undefined) return moment.utc(value.$date);
  if (value instanceof Date || typeof value === "number") return moment.utc(value);

  if (typeof value === "string") {
    const parsed = moment(value, [FUEL_SPEC_DATE_FORMAT, moment.ISO_8601], true);
    return parsed.isValid() ? parsed : null;
  }

  return null;
};

const formatFuelSpecDate = (value) => {
  const parsed = parseFuelSpecDate(value);
  return parsed ? parsed.format(FUEL_SPEC_DATE_FORMAT) : value;
};

export interface FuelSpecObject {
  _id: string;
  bdn_number: string;
  fuel_grade: string;
  iso_fuel_grade: string;
  fuel_class: string;
  viscosity: number;
  density: number;
  lcv: number;
  e_value?: number,
  cf_co2: number;
  cf_ch4: number;
  cf_n2o: number;
  created?: any;
  updated?: any;
  sulphur: number;
  mass_at_time_of_blend: number;
  parent_1?: string;
  parent_2?: string;
  parent_1_mass?: number;
  parent_2_mass?: number;
  is_component_of_blend: boolean;
  default_spec_id?: string;
  source?: string;
  vessel: string;
}

export const convertToFuelSpec = (bdn, scope): FuelSpecObject => ({
  _id: normalizeId(bdn.fuel_spec_id) ?? new ObjectId().toString(),
  bdn_number: bdn.bdn_number,
  fuel_grade: bdn.fuel_grade,
  iso_fuel_grade: bdn.iso_fuel_grade,
  fuel_class: bdn.fuel_class || fuelTypesToFuelClasses[bdn.fuel_grade],
  viscosity: bdn.viscosity,
  density: bdn.density,
  lcv: bdn.lcv,
  e_value: bdn.e_value,
  cf_co2: bdn.cf_co2,
  cf_ch4: bdn.cf_ch4,
  cf_n2o: bdn.cf_n2o,
  sulphur: bdn.sulphur_after_bunkering ?? bdn.sulphur_before_bunkering ?? 0,
  mass_at_time_of_blend: bdn.amount ?? bdn.quantity_received ?? bdn.amount_before_bunkering,
  parent_1: bdn.parent_1,
  parent_2: bdn.parent_2,
  parent_1_mass: bdn.parent_1_mass,
  parent_2_mass: bdn.parent_2_mass,
  is_component_of_blend: bdn.is_component_of_blend ?? false,
  vessel: scope.vesselSpecs?._id.$oid,
});

export const compileFuelSpecs = (scope) => {
  if (!scope.report.bdn_based_reporting) return;
  scope.fuelSpecIdMap = getFuelSpecIdMap(scope);  
  scope.initialBdnFuelSpecMap = getInitialBdnFuelSpecMap(scope);

  const {bunkerSpecs, blendSpecs} = gatherBunkerAndBlends(scope);
  const {tanksObject, tankIds, tankSpecs} = gatherTanks(scope);

  const fuelSpecs = [...bunkerSpecs, ...blendSpecs, ...tankSpecs];
  scope.fuelSpecToBdnMap = Object.fromEntries(fuelSpecs.map(fs => [fs.bdn_number, fs]));

  const newBdnSpecs = gatherNewBdns(scope, tankIds, tanksObject);
  const transferSpecs = gatherTankTransfers(scope);
  scope.newBdnSpecs = Object.fromEntries(
    [...newBdnSpecs, ...transferSpecs].map(fs => [fs.bdn_number, fs])
  );

  assignTanksToFuelSpecs(scope);
  const finalSpecs = [...fuelSpecs, ...transferSpecs, ...newBdnSpecs].filter(Boolean);
  const finalSpecMap = Object.fromEntries(finalSpecs.map(fs => [fs.bdn_number, fs]));
  scope.report.fuel_specs = finalSpecs.map(a => resolveParents(a, finalSpecMap));
};


const gatherBunkerAndBlends = (scope) => {
  const bunkers = scope.report.bunker.bdn_based_reporting_bunkered_fuels ?? [];
  const blendSpecs = getBlendComponentFuelSpecs(bunkers, scope);
  scope.blendSpecsMap = Object.fromEntries(blendSpecs.map(a => [a.bdn_number, a]));
  const bunkerSpecs: FuelSpecObject[] = handleBunkers(bunkers, scope);
  return {bunkerSpecs, blendSpecs}
}

const gatherTanks = (scope) => {
  const tanksObject = { ...scope.report.stock.tanks };
  const tankValues = Object.values(scope.report.stock.tanks ?? {});
  const tankIds = Object.keys(scope.report.stock.tanks ?? {});
  const tankSpecs = handleTanks(tankValues, scope);
  return {tanksObject, tankIds, tankSpecs}
}

const gatherNewBdns = (scope, tankIds, tanksObject): FuelSpecObject[] => {
  const bunkeredTankIds = Object.keys(scope.report?.bunker?.tank_to_bunker_map ?? []);

  const tankWasBunkeredInto = (tankId: string): boolean =>
    tanksObject[tankId].new_bdn &&
    bunkeredTankIds.some(id => id === tankId);

  const newBdns = tankIds.map((tkId: string) =>
    tankWasBunkeredInto(tkId) ? tanksObject[tkId] : undefined).filter(Boolean);
  return handleNewBdns(newBdns, scope);
}

const gatherTankTransfers = (scope): FuelSpecObject[] => {
  const transfers = scope.report.stock.tank_transfers ?? [];
  return getTankTransferFuelSpecs(transfers, scope);
}

const handleBunkers = (bunkers, scope): FuelSpecObject[] =>{
  const nonBlendBunkers = bunkers.filter(a => !a.is_component_of_blend);
  const bunkerIdMap = Object.fromEntries(bunkers.map(a => [normalizeId(a._id), a]));
  return nonBlendBunkers.map(bunker => {
    if (!bunker.bdn_number) return;
    const existingSpec = scope.initialBdnFuelSpecMap?.[bunker.bdn_number] ?? null;
    const fuelSpec = convertToFuelSpec(bunker, scope);
    
    const parentsToBdnNums = {
        ...fuelSpec,
        parent_1: bunkerIdMap[fuelSpec.parent_1]?.bdn_number || undefined,
        parent_2: bunkerIdMap[fuelSpec.parent_2]?.bdn_number || undefined,
      }
    const newSpec: FuelSpecObject = {
      ...resolveParents(parentsToBdnNums, scope.blendSpecsMap),
      mass_at_time_of_blend: bunker.quantity_received,
    }
    return buildUpdatedFuelSpec(existingSpec, newSpec)
  }).filter(Boolean);
}

const getBlendComponentFuelSpecs =
  (bunkers, scope): FuelSpecObject[] => {
    const comps = bunkers.filter((a) => a.is_component_of_blend);
    return comps.map((comp) => {
      const existingSpec = scope.initialBdnFuelSpecMap?.[comp.bdn_number] ?? null;
      const newSpec: FuelSpecObject = {
        ...convertToFuelSpec(comp, scope),
        is_component_of_blend: true,
        mass_at_time_of_blend: comp.amount
      }
      return buildUpdatedFuelSpec(existingSpec, newSpec)
    });
  };

const getTankTransferFuelSpecs = (tankTransfers, scope): FuelSpecObject[] =>
  tankTransfers.map((transfer) => {
    if (!transfer.bdn || !transfer.bdn?.bdn_number) return;

    const fuelSpec = convertToFuelSpec(transfer.bdn, scope);
    fuelSpec.bdn_number = transfer.bdn.bdn_no_suffix;
    const newFuelSpec = resolveParents(fuelSpec, scope.fuelSpecToBdnMap, transfer.quantity);
    const existingFuelSpec = scope.initialBdnFuelSpecMap?.[transfer.bdn.bdn_no_suffix] ?? null;
    return buildUpdatedFuelSpec(existingFuelSpec, newFuelSpec);
  }).filter(Boolean);

const handleNewBdns = (tanks, scope): FuelSpecObject[] =>
  tanks.map(tank => {
    const fuelSpec = convertToFuelSpec(tank.new_bdn, scope);
    fuelSpec.parent_2 ||= tank.bdn_number;
    const newFuelSpec = {
      ...resolveParents(fuelSpec, scope.fuelSpecToBdnMap, tank.quantity_added_in_tank),
      mass_at_time_of_blend: tank.amount_before_bunkering + tank.quantity_added_in_tank
    }
    const existingFuelSpec = scope.initialBdnFuelSpecMap?.[tank.new_bdn.bdn_number] ?? null;
    return buildUpdatedFuelSpec(existingFuelSpec, newFuelSpec)
  });

const resolveParents =
  (
    result: FuelSpecObject,
    specMap: Record<string, FuelSpecObject>,
    parent1Mass?: number,
  )
    : FuelSpecObject => {
    const parentIsResolved = (p) => (p && ObjectId.isValid(p));
    if (parentIsResolved(result.parent_1) && parentIsResolved(result.parent_2)) {
      return result
    }
    const parent1 = specMap[result.parent_1];
    const parent2 = specMap[result.parent_2];
    return {
      ...result,
      parent_1: normalizeId(parent1?._id),
      parent_1_mass: parent1Mass ?? parent1?.mass_at_time_of_blend,
      parent_2: normalizeId(parent2?._id),
      parent_2_mass: parent2?.mass_at_time_of_blend,
    }
  }

const withSpecId = (obj: TankStockObject, map: Record<string, FuelSpecObject>) => {
  return obj ? {...obj, fuel_spec_id: getSpecId(obj, map)} : obj;
}

const getSpecId = (tkObj: TankStockObject, map: Record<string, FuelSpecObject>) =>{
  return map[tkObj.bdn_number]?._id;
}

const assignTanksToFuelSpecs = (scope): void => {
  const tanks = Object.entries(scope.report.stock.tanks ?? {});
  scope.report.stock.tanks = Object.fromEntries(
    tanks.map(([key, tk]: [string, TankStockObject]) => [
      key, 
      {
        ...tk,
        fuel_spec_id: getSpecId(tk, scope.fuelSpecToBdnMap),
        bdn_added_in_tank: withSpecId(tk.bdn_added_in_tank, scope.fuelSpecToBdnMap),
        new_bdn: withSpecId(tk.new_bdn, scope.newBdnSpecs),
      }
    ])
  );
};

const getFuelSpecIdMap = (scope): Record<string, FuelSpecObject> => {
  const carryOver = getCarryOver(scope);
  const fuelSpecs = scope.report.fuel_specs ?? carryOver?.fuel_specs ?? [];
  return Object.fromEntries(fuelSpecs.map((fs: FuelSpecObject) => [normalizeId(fs._id), fs]));
}

const getInitialBdnFuelSpecMap = (scope): Record<string, FuelSpecObject> => {
  if (!scope.report.fuel_specs) return undefined;
  return Object.fromEntries(scope.report.fuel_specs.map(a => [a.bdn_number, a]))
}

const handleTanks = (tanks, scope): FuelSpecObject[] => {
  return tanks.map((tank: TankStockObject) => {
    const specId = normalizeId(tank.fuel_spec_id)
    const existingSpec = scope.fuelSpecIdMap[specId] ?? null;
    const newSpec = convertToFuelSpec(tank, scope);
    return buildUpdatedFuelSpec(existingSpec, newSpec);
  })
}

const fuelSpecHasChanged = ( prev: FuelSpecObject, curr: FuelSpecObject ): boolean => {
  if (normalizeId(prev._id) !== normalizeId(curr._id)) return true;
  return FUEL_SPEC_COMPARISON_KEYS.some(key => prev[key] !== curr[key]);
};

const buildUpdatedFuelSpec = (
  existing: FuelSpecObject,
  current: FuelSpecObject
): FuelSpecObject => {
  if (!existing) {
    const now = formatFuelSpecDate(new Date());
    return { ...current, created: now, updated: now };
  };

  const hasChanged = fuelSpecHasChanged(existing, current);
  return {
    ...(!hasChanged ? existing : current),
    _id: normalizeId(existing._id),
    created: formatFuelSpecDate(existing.created),
    updated: !hasChanged ? formatFuelSpecDate(existing.updated) : formatFuelSpecDate(new Date()),
    parent_1: existing.parent_1 ? normalizeId(existing.parent_1) : undefined,
    parent_2: existing.parent_2 ? normalizeId(existing.parent_2) : undefined,
    source: normalizeId(existing.source),
    vessel: normalizeId(existing.vessel),
  };
};